// The outbox (docs/WEBMAIL.md, phase 4): mail written and actions taken (read, star, move, trash) while the server
// cannot be reached wait on the device (store.ts: outbox, outfiles) and go to the server in the order they were made
// once it answers again: when the app starts, comes back online and after every /api/folders answer (Safari has no
// Background Sync, so only while the app is open). One flush at a time per browser (Web Locks).
//   mail     sent with the idempotency key it was written with, so a message whose answer was lost is never sent
//            twice. One the server refuses (a recipient Yahoo does not take, say) waits to be edited or deleted.
//   actions  shown in the offline copy at once and sent with the folder the message was in (`from`): the server does
//            nothing when it is no longer there, so an action sent again after a lost answer, or overtaken by another
//            device, never moves or deletes the message from where it is now. Until it is sent, the offline sync
//            leaves the message as the action left it; cancelling the action puts the copy back as it was. The server's
//            listings show it too until it has left the outbox (status.waiting, overrides.ts), so back online the mail
//            does not show as it was while the outbox is being sent.
// Actions taken while the server answers are done in the background, one at a time (perform, markFolderRead). Each is
// kept on the device until it is done (store.ts: turns), so one the app was closed before it came to (swiped away, or
// ended by the system in the background, as the iPhone does) is done when the app starts again (resumeTurns).
// An action on several messages (the messages chosen in a list: store.BulkAction) is one action here too: one request,
// one entry in the outbox, kept and sent as one; the server answers for each message, and the copy and the listings
// follow each message's outcome (the ones it could not do go back to how they were).

import { useEffect, useState } from "preact/hooks";
import { type ActionResult, ApiError, type BulkOutcome, type BulkResult, type FoldersResponse, type SendRequest, type SendResult, api } from "./api";
import { API_BASE, SignedOut } from "./auth";
import { messages as countOf } from "./bulk";
import { forgetContacts } from "./contacts";
import { NetworkError, isNetworkError, isOnline } from "./net";
import { mergeFolders } from "./offline";
import { type Draft, dropDraftFile, keepDraftFiles, newKey, saveDraft } from "./outgoing";
import { type Change, type Overrides, begin, settle } from "./overrides";
import type { ComposeMode } from "./route";
import { type BulkAction, type CachedLocation, type CopyUndo, type FolderRead, type OutboxAction, type OutboxEntry, type OutboxMail, type SingleAction, type TurnEntry, copyChanged, dateKey, db, getMeta, messagesOf, singleOn, withRoom } from "./store";

/** After a failure the server may get over (a 5xx): first tried again after this long, then twice as long each time. */
const RETRY_FIRST_MS = 30_000;
const RETRY_MAX_MS = 15 * 60_000;
/** Mail the server is still sending, or whose attempt broke off (409 sending, uncertain): asked again after this. */
const RETRY_SENDING_MS = 60_000;

export type { BulkAction, OutboxAction, OutboxEntry, SingleAction } from "./store";

export interface OutboxStatus {
  /** Everything waiting, oldest first. */
  entries: OutboxEntry[];
  /** The entry being sent now. */
  sending: number | null;
  /** The changes of the actions waiting on the device (in the outbox, or kept for their turn by another page, one that
   * was closed say), by message id: shown over the server's listings, which do not have them yet (overrides.ts). */
  waiting: Overrides;
}

let status: OutboxStatus = { entries: [], sending: null, waiting: new Map() };
const watchers = new Set<(s: OutboxStatus) => void>();

function setStatus(patch: Partial<OutboxStatus>): void {
  status = { ...status, ...patch };
  watchers.forEach((fn) => fn(status));
}

export function useOutbox(): OutboxStatus {
  const [s, set] = useState(status);
  useEffect(() => {
    watchers.add(set);
    set(status);
    return () => void watchers.delete(set);
  }, []);
  return s;
}

/** What a flush did, for the app to tell. `flushed`: something left the outbox (the folders have changed). `failed`
 * without an entry: an action the app was closed before it came to (resumeTurns). */
export type OutboxEvent =
  | { type: "sent"; entry: OutboxEntry; result: SendResult }
  | { type: "failed"; entry?: OutboxEntry; error: string }
  | { type: "flushed" };

const listeners = new Set<(e: OutboxEvent) => void>();

export function onOutbox(fn: (e: OutboxEvent) => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

function emit(e: OutboxEvent): void {
  listeners.forEach((fn) => fn(e));
}

/** IndexedDB can be unavailable (private windows, storage turned off): then nothing waits, and nothing can. */
async function safe<T>(run: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await run();
  } catch (e) {
    console.warn("outbox:", e);
    return fallback;
  }
}

export function outboxStatus(): OutboxStatus {
  return status;
}

let reading: Promise<unknown> = Promise.resolve();

/** Reads what waits in the outbox and what other pages keep for their turn, one read after another, so the status
 * never goes back to an older one. Returns the outbox. */
export function refreshOutbox(): Promise<OutboxEntry[]> {
  const read = reading.then(readOutbox);
  reading = read.catch(() => {});
  return read;
}

async function readOutbox(): Promise<OutboxEntry[]> {
  const [entries, turns] = await safe(async () => {
    const d = db();
    return d.transaction("r", d.outbox, d.turns, () => Promise.all([d.outbox.orderBy("seq").toArray(), d.turns.orderBy("seq").toArray()]));
  }, [[], []] as [OutboxEntry[], TurnEntry[]]);
  setStatus({ entries, waiting: follow(entries, turns) });
  return entries;
}

/** The actions waiting on the device as last read, by where they wait ("o12": in the outbox, "t3": kept for its turn
 * by another page; this page's own are shown by the app until they are done or queued), with the messages each is on. */
const following = new Map<string, { messages: string[]; change: Change }>();
/** The messages of actions that left without being done (refused, cancelled; some of an action on several): their
 * change is taken back. */
const undone = new Map<string, Set<string>>();

function markUndone(key: string, ids: string[]): void {
  const set = undone.get(key) ?? new Set<string>();
  for (const id of ids) set.add(id);
  undone.set(key, set);
}

function changeOf(a: OutboxAction): Change {
  if (a.kind === "read" || a.kind === "read-many") return { unread: !a.read };
  if (a.kind === "flag" || a.kind === "flag-many") return { flagged: a.flagged };
  return { removed: true };
}

/** The waiting changes after a read: an action that came shows from now, one that left is settled now, so a listing
 * asked for after it left shows what the server has, and one asked for before still shows the action. Messages that
 * left an action on several (cancelled, or the server could not do them) are taken back as it goes on without them. */
function follow(entries: OutboxEntry[], turns: TurnEntry[]): Overrides {
  const now = new Map<string, { messages: string[]; change: Change; created: string }>();
  for (const e of entries) if (e.kind !== "mail") now.set(`o${e.seq}`, { messages: messagesOf(e), change: changeOf(e), created: e.created_at });
  for (const t of turns) {
    if (t.session !== SESSION && t.action.kind !== "folder-read") now.set(`t${t.seq}`, { messages: messagesOf(t.action), change: changeOf(t.action), created: t.created_at });
  }
  let waiting = status.waiting;
  for (const [key, w] of following) {
    const still = now.get(key);
    if (still) {
      const gone = w.messages.filter((m) => !still.messages.includes(m));
      for (const m of gone) waiting = settle(waiting, m, w.change, false);
      if (gone.length > 0) following.set(key, { ...w, messages: still.messages });
      continue;
    }
    for (const m of w.messages) waiting = settle(waiting, m, w.change, !undone.get(key)?.has(m));
    following.delete(key);
  }
  // in the order they were taken, so the last one on a message wins
  const came = [...now].filter(([key]) => !following.has(key)).sort(([, x], [, y]) => x.created.localeCompare(y.created));
  for (const [key, w] of came) {
    for (const m of w.messages) waiting = begin(waiting, m, w.change);
    following.set(key, { messages: w.messages, change: w.change });
  }
  for (const key of undone.keys()) if (!following.has(key)) undone.delete(key);
  return waiting;
}

/** Sign-out: the outbox went with the copy (store.deleteOfflineCopy). */
export function forgetOutbox(): void {
  following.clear();
  undone.clear();
  setStatus({ entries: [], sending: null, waiting: new Map() });
}

/** "Move “Subject” to Archive", "Move 73 messages from Amazon to Trash": for the outbox screen and the notices. */
export function describeAction(a: OutboxAction & { subject: string | null }): string {
  const what = "messages" in a ? countOf(a.messages.length) : `“${a.subject || "(no subject)"}”`;
  if (a.kind === "read" || a.kind === "read-many") return `Mark ${what} as ${a.read ? "read" : "unread"}`;
  if (a.kind === "flag" || a.kind === "flag-many") return a.flagged ? `Star ${what}` : `Remove the star from ${what}`;
  if (a.kind === "move" || a.kind === "move-many") return `Move ${what} from ${a.from} to ${a.folder}`;
  return `Move ${what} from ${a.from} to Trash`;
}

/** The message of a bulk action's failures for a notice: which action, how many were not done and the first reason. */
function failedText(a: OutboxAction & { subject: string | null }, failed: BulkOutcome[]): string {
  return `${describeAction(a)}: ${countOf(failed.length)} not done${failed[0]?.reason ? `: ${failed[0].reason}` : ""}`;
}

// ---------------------------------------------------------------------------
// actions
// ---------------------------------------------------------------------------

/** The folder an action on a message starts from: the one it was opened from when it is there, else its first place
 * outside Trash (as the server picks), else its first; null when it is in no folder. */
export function actionFolder(folders: { folder: string }[], opened: string | null): string | null {
  const names = folders.map((f) => f.folder);
  return names.find((f) => f === opened) ?? names.find((f) => !["trash", "deleted"].includes(f.toLowerCase())) ?? names[0] ?? null;
}

export interface Performed {
  /** In the outbox: no connection (or no answer). */
  queued: boolean;
  /** The server did nothing, and why (the message was no longer in the folder). */
  skipped: string | null;
  /** An action on several messages, done now: what became of each. */
  outcomes?: BulkOutcome[];
}

function run(a: SingleAction): Promise<ActionResult>;
function run(a: BulkAction): Promise<BulkResult>;
function run(a: OutboxAction): Promise<ActionResult | BulkResult>;
function run(a: OutboxAction): Promise<ActionResult | BulkResult> {
  switch (a.kind) {
    case "read":
      return api.read(a.message, a.read);
    case "flag":
      return api.flag(a.message, a.flagged);
    case "move":
      return api.move(a.message, a.folder, a.from);
    case "trash":
      return api.trash(a.message, a.from);
    case "read-many":
      return api.readMany(a.messages, a.read);
    case "flag-many":
      return api.flagMany(a.messages, a.flagged);
    case "move-many":
      return api.moveMany(a.messages, a.folder, a.from);
    default:
      return api.trashMany(a.messages, a.from);
  }
}

let turn: Promise<unknown> = Promise.resolve();

/** Runs one request to Yahoo after those asked for before it have answered. The app does not wait for its actions (a
 * trash leaves the message at once, the next one can follow straight away), so they would otherwise run side by side:
 * one at a time none overtakes an earlier one (a read of a message before its move), and Yahoo gets one at a time. */
export function inTurn<T>(run: () => Promise<T>): Promise<T> {
  const result = turn.then(run);
  turn = result.catch(() => {});
  return result;
}

/** An action on a message (or on several at once), in turn with the others (inTurn): done now when the server answers
 * and no action waits in the outbox before it, else put in the outbox (sending it again after a lost answer is safe).
 * Either way the offline copy shows it once it is done or queued (of several, the messages the server did it on).
 * Until then it is kept on the device, to be done when the app starts again should it be closed first (resumeTurns).
 * What the server refuses is thrown; without a connection and without IndexedDB, NetworkError. */
export function perform(a: OutboxAction, subject: string | null): Promise<Performed> {
  return inTurnKept(a, subject, (turn) => performNow(a, subject, turn));
}

/** A whole folder marked read, in turn with the actions on messages and kept until it is done as they are: only up to
 * `upto` (from /api/folders, as the count the user was asked about), so done late it never marks the mail that came
 * since. Without `upto` (a folder never synced, or folders listed by a server from before it) it is not kept. It needs
 * the connection: it is not one for the outbox. */
export function markFolderRead(folder: string, upto: string | null | undefined): Promise<{ folder: string; marked: number }> {
  const run = () => api.markFolderRead(folder, upto ?? null);
  return upto ? inTurnKept({ kind: "folder-read", folder, upto }, null, run) : inTurn(run);
}

/** Runs an action in turn (inTurn), kept on the device from now until it is done (resumeTurns). */
function inTurnKept<T>(a: OutboxAction | FolderRead, subject: string | null, run: (turn: number | null) => Promise<T>): Promise<T> {
  const kept = keep(a, subject);
  return inTurn(async () => {
    const seq = await kept;
    try {
      return await run(seq);
    } finally {
      if (seq !== null) await safe(() => db().turns.delete(seq), undefined);
      unclaim();
    }
  });
}

/** Keeps an action on the device until it is done; null when IndexedDB cannot (it then waits in memory only). */
async function keep(a: OutboxAction | FolderRead, subject: string | null): Promise<number | null> {
  await claim();
  return safe(() => db().turns.add({ session: SESSION, action: a, subject, created_at: new Date().toISOString() }), null);
}

/** `turn`: the action as kept while it waited for its turn, which leaves turns when it goes to the outbox. */
async function performNow(a: OutboxAction, subject: string | null, turn: number | null): Promise<Performed> {
  let offline: unknown = new NetworkError();
  if (isOnline() && (await safe(waitingActions, 0)) === 0) {
    try {
      if ("messages" in a) {
        const result = await run(a);
        const done = result.results.filter((r) => r.status === "done").map((r) => r.id);
        if (done.length > 0) await safe(() => inCopy(() => applyToCopy({ ...a, messages: done })), undefined);
        return { queued: false, skipped: null, outcomes: result.results };
      }
      const result = await run(a);
      if (!result.skipped) await safe(() => inCopy(() => applyToCopy(a)), undefined);
      return { queued: false, skipped: result.skipped ?? null };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
      offline = e;
    }
  }
  try {
    await enqueue(a, subject, turn);
  } catch (e) {
    console.warn("outbox:", e);
    throw offline;
  }
  void flushOutbox();
  return { queued: true, skipped: null };
}

async function waitingActions(): Promise<number> {
  return db().outbox.filter((e) => e.kind !== "mail").count();
}

async function enqueue(a: OutboxAction, subject: string | null, turn: number | null): Promise<void> {
  await inCopy(async () => {
    const undo = await applyToCopy(a);
    await db().outbox.add({ ...a, subject, created_at: new Date().toISOString(), attempts: 0, error: null, refused: false, next_at: 0, ...undo });
    if (turn !== null) await db().turns.delete(turn); // it waits in the outbox now, and only there
  });
  await refreshOutbox();
}

/** This page among the app's open pages (tabs, the installed app): the actions it keeps waiting are its own. */
const SESSION = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

function turnLock(session: string): string {
  return `ym-turns:${API_BASE}:${session}`;
}

/** The actions this page keeps waiting, and the lock it holds while there are any: another page takes them over only
 * once the lock is free, when this page is gone. */
let claims = 0;
let holding: { granted: Promise<void>; release: () => void } | null = null;

function claim(): Promise<void> {
  claims++;
  if (holding) return holding.granted;
  if (typeof navigator === "undefined" || !navigator.locks) return Promise.resolve();
  let release = () => {};
  const held = new Promise<void>((resolve) => (release = resolve));
  const granted = new Promise<void>((resolve) => {
    navigator.locks
      .request(turnLock(SESSION), () => {
        resolve();
        return held;
      })
      .catch(() => resolve());
  });
  holding = { granted, release };
  return granted;
}

function unclaim(): void {
  if (--claims > 0 || !holding) return;
  holding.release();
  holding = null;
}

/** Runs `take` when the page `session` is gone, holding its lock so no other page takes its actions at the same time;
 * nothing while it is open. Without Web Locks a page cannot tell, and takes them (doing one twice is safe). */
async function ifGone(session: string, take: () => Promise<void>): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.locks) return take();
  await navigator.locks.request(turnLock(session), { ifAvailable: true }, async (lock) => {
    if (lock) await take();
  });
}

/** Does the actions a page kept waiting when it was closed before their turn came (swiped away, or ended by the system
 * in the background): in turn with this page's own, oldest first, each as perform does it, now while the server
 * answers, else in the outbox. A folder marked read needs the server: without it, it and the actions after it wait for
 * the next time. One the server refuses is told (a read is not, as in the outbox); one it finds done already (no
 * longer in the folder it was moved from) is not. Those of a page still open (another tab) are left to it. When the
 * app starts, comes to the front and comes back online. */
export function resumeTurns(): Promise<void> {
  return inTurn(() => safe(resumeNow, undefined));
}

async function resumeNow(): Promise<void> {
  const d = db();
  const left = (await d.turns.orderBy("seq").toArray()).filter((t) => t.session !== SESSION);
  let changed = false;
  for (const session of new Set(left.map((t) => t.session))) {
    await ifGone(session, async () => {
      for (const t of left.filter((x) => x.session === session)) {
        if (!(await d.turns.get(t.seq!))) continue; // another page took it over meanwhile
        try {
          if (t.action.kind === "folder-read") await api.markFolderRead(t.action.folder, t.action.upto);
          else {
            const done = await performNow(t.action, t.subject, t.seq!);
            const failed = done.outcomes?.filter((r) => r.status === "failed") ?? [];
            if (failed.length > 0) {
              emit({ type: "failed", error: failedText({ ...t.action, subject: t.subject }, failed) });
              markUndone(`t${t.seq}`, failed.map((r) => r.id));
            }
          }
          changed = true;
        } catch (e) {
          // neither the server nor the outbox took it: it waits for the next time, and the later ones behind it
          if (isNetworkError(e) || e instanceof SignedOut) return;
          if (t.action.kind !== "read") emit({ type: "failed", error: `${describeTurn(t)}: ${e instanceof Error ? e.message : String(e)}` });
          if (t.action.kind !== "folder-read") markUndone(`t${t.seq}`, messagesOf(t.action));
        }
        await d.turns.delete(t.seq!);
      }
    });
  }
  if (left.length > 0) await refreshOutbox(); // the listings show what is done as the server has it
  if (changed) emit({ type: "flushed" });
}

function describeTurn(t: TurnEntry): string {
  return t.action.kind === "folder-read" ? `Mark all in ${t.action.folder} as read` : describeAction({ ...t.action, subject: t.subject });
}

/** A change to the copy and the outbox in one transaction; a search kept in memory is done again after it. */
async function inCopy<T>(change: () => Promise<T>): Promise<T> {
  const d = db();
  const result = await d.transaction("rw", [d.outbox, d.outfiles, d.locations, d.rows, d.folders, d.meta, d.turns], change);
  copyChanged();
  return result;
}

/** `flags` with `flag` set (on) or cleared. */
function withFlag(flags: string[], flag: string, on: boolean): string[] {
  const rest = flags.filter((f) => f !== flag);
  return (on ? [...rest, flag] : rest).sort();
}

/** Trash as the folders the app last had name it (the server looks for "Trash", then "Deleted"). */
async function trashFolder(): Promise<string> {
  const names = ((await getMeta<FoldersResponse>("folders"))?.folders ?? []).map((f) => f.name);
  return names.find((n) => n.toLowerCase() === "trash") ?? names.find((n) => n.toLowerCase() === "deleted") ?? "Trash";
}

/** Shows an action in the copy, and returns what to put back to undo it: `undo` for one message, `undos` by message for
 * an action on several. */
async function applyToCopy(a: OutboxAction): Promise<Pick<OutboxEntry, "undo" | "undos">> {
  if (!("messages" in a)) return { undo: await applyOneToCopy(a) };
  const undos: Record<string, CopyUndo> = {};
  for (const m of a.messages) undos[m] = await applyOneToCopy(singleOn(a, m));
  return { undos };
}

/** What an entry changed in the copy for one of its messages. */
function undoOf(e: OutboxEntry, message: string): CopyUndo | undefined {
  if (e.kind === "mail") return undefined;
  return "messages" in e ? e.undos?.[message] : e.message === message ? e.undo : undefined;
}

async function setUndo(e: OutboxEntry, message: string, undo: CopyUndo): Promise<void> {
  if (e.kind === "mail") return;
  if ("messages" in e) {
    e.undos = { ...e.undos, [message]: undo };
    await db().outbox.update(e.seq!, { undos: e.undos });
  } else await db().outbox.update(e.seq!, { undo });
}

/** Shows an action on one message in the copy: the message's flags, or its places (in an offline folder it arrives in,
 * when the copy has it). Returns what to put back to undo it. */
async function applyOneToCopy(a: SingleAction): Promise<CopyUndo> {
  const d = db();
  const [locations, row, states] = await Promise.all([d.locations.where("id").equals(a.message).toArray(), d.rows.get(a.message), d.folders.toArray()]);
  const undo: CopyUndo = { locations, row: row ? { folders: row.folders, flags: row.flags, unread: row.unread, flagged: row.flagged } : null };
  const offline = new Set(states.map((s) => s.name));
  const shown = row ? mergeFolders(row.folders, locations, offline) : locations.map((l) => ({ folder: l.folder, flags: l.flags }));
  let next: CachedLocation[];
  let places: { folder: string; flags: string[] }[];
  if (a.kind === "read" || a.kind === "flag") {
    const [flag, on] = a.kind === "read" ? ["\\Seen", a.read] : ["\\Flagged", a.flagged];
    next = locations.map((l) => ({ ...l, flags: withFlag(l.flags, flag, on) }));
    places = shown.map((f) => ({ ...f, flags: withFlag(f.flags, flag, on) }));
  } else {
    const trash = await trashFolder();
    const from = a.from.toLowerCase();
    // a trash moves the message to Trash from every folder, and deletes it from Trash itself
    const leaves = (folder: string) => a.kind === "trash" || folder.toLowerCase() === from;
    const target = a.kind === "move" ? a.folder : trash;
    const arrives = a.kind === "move" || shown.some((f) => f.folder.toLowerCase() !== trash.toLowerCase());
    const flags = shown.find((f) => leaves(f.folder))?.flags ?? [];
    places = shown.filter((f) => !leaves(f.folder) && f.folder !== target);
    next = locations.filter((l) => !leaves(l.folder) && l.folder !== target);
    if (arrives) {
      places.push({ folder: target, flags });
      if (row && offline.has(target)) next.push({ folder: target, id: a.message, date_key: dateKey(row.date), flags });
    }
  }
  await d.locations.where("id").equals(a.message).delete();
  await d.locations.bulkPut(next);
  if (row) {
    const inYahoo = places.length > 0;
    const flags = inYahoo ? Array.from(new Set(places.flatMap((f) => f.flags))).sort() : row.flags;
    await d.rows.update(a.message, {
      folders: places,
      flags,
      unread: inYahoo ? !flags.includes("\\Seen") : row.unread,
      flagged: inYahoo ? flags.includes("\\Flagged") : row.flagged,
    });
  }
  return undo;
}

/** Puts the copy of a message back to how it was before an action (only in folders still kept offline). */
async function undoInCopy(id: string, undo: CopyUndo | undefined): Promise<void> {
  if (!undo) return;
  const d = db();
  const offline = new Set((await d.folders.toArray()).map((s) => s.name));
  await d.locations.where("id").equals(id).delete();
  await d.locations.bulkPut(undo.locations.filter((l) => offline.has(l.folder)));
  if (undo.row) await d.rows.update(id, undo.row);
}

/** The offline folders these messages of an entry (all of them, if not given) are in or were in before its actions:
 * listed again at the next sync, since the server may have changed them while the sync left the messages alone. */
async function relist(entry: OutboxEntry, ids = messagesOf(entry as OutboxAction)): Promise<void> {
  if (entry.kind === "mail") return;
  const d = db();
  const now = await d.locations.where("id").anyOf(ids).toArray();
  const before = ids.flatMap((m) => undoOf(entry, m)?.locations ?? []);
  const names = Array.from(new Set([...now, ...before].map((l) => l.folder)));
  if (names.length > 0) await d.folders.where("name").anyOf(names).modify({ complete: false });
}

/** Takes an action out of the outbox, and the copy back to before it: the later actions on the same messages are
 * undone first and done again after. With `only`, those messages of an action on several leave it and the rest stay
 * (the server could not do them; a user cancels the whole). Mail is deleted with its files. */
export async function cancel(seq: number, only?: string[]): Promise<void> {
  const d = db();
  let ids: string[] = [];
  await inCopy(async () => {
    const entry = await d.outbox.get(seq);
    if (!entry) return;
    if (entry.kind === "mail") {
      await d.outbox.delete(seq);
      await d.outfiles.where("key").equals(entry.request.idempotency_key).delete();
      return;
    }
    const all = messagesOf(entry);
    ids = only ? all.filter((m) => only.includes(m)) : all;
    const later = (await d.outbox.where("seq").above(seq).toArray()).filter((e) => e.kind !== "mail");
    for (const m of ids) {
      const after = later.filter((e) => messagesOf(e as OutboxAction).includes(m));
      for (const e of [...after].reverse()) await undoInCopy(m, undoOf(e, m));
      await undoInCopy(m, undoOf(entry, m));
      for (const e of after) await setUndo(e, m, await applyOneToCopy(singleOn(e as OutboxAction, m)));
    }
    const left = all.filter((m) => !ids.includes(m));
    if (left.length === 0 || !("messages" in entry)) await d.outbox.delete(seq);
    else {
      const undos = Object.fromEntries(left.map((m) => [m, entry.undos?.[m]]).filter(([, u]) => u)) as Record<string, CopyUndo>;
      await d.outbox.put({ ...entry, messages: left, undos });
    }
    await relist(entry, ids);
  });
  markUndone(`o${seq}`, ids); // not done: the listings no longer show it either
  await refreshOutbox();
}

// ---------------------------------------------------------------------------
// mail
// ---------------------------------------------------------------------------

/** Puts a message in the outbox with its files from the device (the same message again replaces it): it is sent once
 * the app can reach the server, at once if it can. Storage that runs out is made from the oldest mail in the copy. */
export async function queueMail(mail: Omit<OutboxMail, "kind" | "files">, files: File[]): Promise<void> {
  const d = db();
  const key = mail.request.idempotency_key;
  const stored = await Promise.all(files.map(async (f, n) => ({ key, n, name: f.name, mime: f.type || "application/octet-stream", data: await f.arrayBuffer() })));
  const entry: OutboxEntry = {
    kind: "mail",
    ...mail,
    files: stored.map((f) => ({ name: f.name, mime: f.mime, size: f.data.byteLength })),
    subject: mail.request.subject || null,
    created_at: new Date().toISOString(),
    attempts: 0,
    error: null,
    refused: false,
    next_at: 0,
  };
  await withRoom("￿", () =>
    d.transaction("rw", d.outbox, d.outfiles, async () => {
      const same = await d.outbox.filter((e) => e.kind === "mail" && e.request.idempotency_key === key).primaryKeys();
      await d.outbox.bulkDelete(same);
      await d.outfiles.where("key").equals(key).delete();
      await d.outfiles.bulkPut(stored);
      await d.outbox.add(entry);
    }),
  );
  await refreshOutbox();
  void flushOutbox();
}

/** Takes a message out of the outbox to write it again: it becomes a draft on the device (with its idempotency key:
 * should an earlier attempt have gone out after all, the server says so), its files from the device with it. Where to
 * write it (the draft's id with it), or null when it is gone (sent meanwhile). StorageFull, and the message stays, when
 * its files find no room with the draft; an error, and the message stays, when the draft finds none. */
export async function editMail(seq: number): Promise<{ mode: ComposeMode; source: string | null; draft: string } | null> {
  const d = db();
  const entry = await d.outbox.get(seq);
  if (!entry || entry.kind !== "mail" || status.sending === seq) return null;
  const stored = await d.outfiles.where("key").equals(entry.request.idempotency_key).sortBy("n");
  const files = stored.map((f) => ({ id: newKey(), file: new File([f.data], f.name, { type: f.mime }) }));
  await keepDraftFiles(files);
  const draft: Draft = {
    ...entry.draft,
    id: newKey(),
    files: files.map(({ id, file }) => ({ id, name: file.name })),
    lostFiles: [],
    touched: true,
    savedAt: Date.now(),
  };
  if (!saveDraft(draft)) {
    await Promise.all(files.map((f) => dropDraftFile(f.id)));
    throw new Error("No room on the device for the draft: the message stays in the outbox");
  }
  await cancel(seq);
  return { mode: entry.mode, source: entry.source, draft: draft.id };
}

export function bytesToBase64(data: ArrayBuffer): string {
  const bytes = new Uint8Array(data);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

async function request(mail: OutboxMail): Promise<SendRequest> {
  const files = await db().outfiles.where("key").equals(mail.request.idempotency_key).sortBy("n");
  const uploads = files.map((f) => ({ filename: f.name, mime: f.mime, data: bytesToBase64(f.data) }));
  return { ...mail.request, attachments: [...(mail.request.attachments ?? []), ...uploads] };
}

// ---------------------------------------------------------------------------
// sending
// ---------------------------------------------------------------------------

let flushing: Promise<void> | null = null;
let again = false;
let forced = false;

/** Sends what waits in the outbox, oldest first; `now` ("Send now") also what waits after a failure. One flush at a
 * time: a call during one makes it run once more afterwards. */
export function flushOutbox(now = false): Promise<void> {
  if (now) forced = true;
  if (flushing) {
    again = true;
    return flushing;
  }
  flushing = (async () => {
    try {
      do {
        again = false;
        await exclusive(runFlush);
      } while (again);
    } finally {
      flushing = null;
    }
  })();
  return flushing;
}

/** One flush per browser: another tab that is sending is left to it. */
async function exclusive(run: () => Promise<void>): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.locks) return run();
  await navigator.locks.request(`ym-outbox:${API_BASE}`, { ifAvailable: true }, async (lock) => {
    if (lock) await run();
  });
}

async function runFlush(): Promise<void> {
  const force = forced;
  forced = false;
  if (!isOnline()) return;
  const entries = await refreshOutbox(); // another tab may have added some, or sent them
  if (entries.length === 0) return;
  /** Messages with an action left waiting: their later actions wait behind it (a move may depend on the one before). */
  const blocked = new Set<string>();
  let changed = false;
  try {
    for (const entry of entries) {
      const ids = entry.kind === "mail" ? [] : messagesOf(entry);
      if ((entry.refused && !force) || ids.some((m) => blocked.has(m))) continue;
      if (!force && entry.next_at > Date.now()) {
        for (const m of ids) blocked.add(m);
        continue;
      }
      const outcome = await attempt(entry);
      if (outcome !== "unchanged") changed = true;
      if (outcome === "waits") for (const m of ids) blocked.add(m);
    }
  } catch (e) {
    // no connection, or signed out: the rest waits for the next flush
    if (!(e instanceof SignedOut) && !isNetworkError(e)) console.error("outbox:", e);
  } finally {
    setStatus({ sending: null });
    await refreshOutbox();
    if (changed) emit({ type: "flushed" });
  }
}

function backoff(attempts: number): number {
  return Date.now() + Math.min(RETRY_FIRST_MS * 2 ** (attempts - 1), RETRY_MAX_MS);
}

/** Sends one entry. "done": it left the outbox; "waits": it stays (to be tried again, or refused mail); "unchanged":
 * it was gone already (cancelled, edited or sent by another tab). No answer and signing out are thrown. */
async function attempt(entry: OutboxEntry): Promise<"done" | "waits" | "unchanged"> {
  const d = db();
  const seq = entry.seq!;
  if (!(await d.outbox.get(seq))) return "unchanged";
  setStatus({ sending: seq });
  try {
    if (entry.kind === "mail") {
      const result = await api.send(await request(entry));
      await cancel(seq);
      forgetContacts();
      emit({ type: "sent", entry, result });
    } else if ("messages" in entry) {
      const result = await run(entry);
      const failed = result.results.filter((r) => r.status === "failed");
      const skipped = result.results.filter((r) => r.status === "skipped").map((r) => r.id);
      if (failed.length > 0) await cancel(seq, failed.map((r) => r.id)); // not done: the copy goes back for them
      await d.outbox.delete(seq);
      if (skipped.length > 0) await relist(entry, skipped); // the server knew better: the sync shows where they are
      if (failed.length > 0) emit({ type: "failed", entry, error: failedText(entry, failed) });
    } else {
      const result = await run(entry);
      await d.outbox.delete(seq);
      if (result.skipped) await relist(entry); // the server knew better: the sync shows where the message is
    }
    return "done";
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
    const attempts = entry.attempts + 1;
    if (entry.kind === "mail") {
      const busy = e.status === 409 && (e.code === "sending" || e.code === "uncertain");
      if (busy || e.status >= 500) {
        // the key makes asking again safe, whatever happened to this attempt
        await d.outbox.update(seq, { attempts, error: e.message, next_at: busy ? Date.now() + RETRY_SENDING_MS : backoff(attempts) });
      } else {
        await d.outbox.update(seq, { attempts, error: e.message, refused: true });
        emit({ type: "failed", entry, error: `Not sent: ${e.message}` });
      }
      return "waits";
    }
    if (e.status >= 500) {
      await d.outbox.update(seq, { attempts, error: e.message, next_at: backoff(attempts) });
      return "waits";
    }
    // refused (the message left Yahoo, the folder is gone, ...): the copy goes back to how the server has it
    await cancel(seq);
    if (entry.kind !== "read") emit({ type: "failed", entry, error: `${describeAction(entry)}: ${e.message}` });
    return "done";
  }
}
